import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { logger } from '@/lib/logger';

export const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const REQUIRED_VARS = ['VITE_FIREBASE_API_KEY', 'VITE_FIREBASE_AUTH_DOMAIN', 'VITE_FIREBASE_PROJECT_ID', 'VITE_FIREBASE_STORAGE_BUCKET', 'VITE_FIREBASE_APP_ID'] as const;
const missing = REQUIRED_VARS.filter(key => !import.meta.env[key]);
if (missing.length > 0) {
    throw new Error(`Missing required Firebase environment variables: ${missing.join(', ')}. Check your .env file.`);
}

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app, "us-central1");

// Set auth persistence explicitly for better Firefox compatibility
setPersistence(auth, browserLocalPersistence).catch((error) => {
    logger.error('Failed to set auth persistence:', error);
});

// Emulator connection — auto-detected from env vars (mirrors Admin SDK pattern).
// Set VITE_FIREBASE_AUTH_EMULATOR_HOST etc. in .env to connect to emulators.
// Remove or comment them out to use production Firebase.
const authEmulatorHost = import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST;
const firestoreEmulatorHost = import.meta.env.VITE_FIRESTORE_EMULATOR_HOST;
const storageEmulatorHost = import.meta.env.VITE_FIREBASE_STORAGE_EMULATOR_HOST;

const functionsEmulatorHost = import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_HOST;

if (import.meta.env.DEV && (authEmulatorHost || firestoreEmulatorHost || storageEmulatorHost || functionsEmulatorHost)) {
    logger.log('🔧 Using Firebase Emulators');
    if (authEmulatorHost) {
        connectAuthEmulator(auth, `http://${authEmulatorHost}`, { disableWarnings: false });
    }
    if (firestoreEmulatorHost) {
        const [host, port] = firestoreEmulatorHost.split(':');
        connectFirestoreEmulator(db, host, parseInt(port, 10));
    }
    if (storageEmulatorHost) {
        const [host, port] = storageEmulatorHost.split(':');
        connectStorageEmulator(storage, host, parseInt(port, 10));
    }
    if (functionsEmulatorHost) {
        const [host, port] = functionsEmulatorHost.split(':');
        connectFunctionsEmulator(functions, host, parseInt(port, 10));
        logger.log(`🔧 Functions → emulator at ${functionsEmulatorHost}`);
    }
} else if (import.meta.env.DEV) {
    logger.log('☁️ Using Production Firebase');
}

export { auth, db, storage, functions };
export const useEmulators = !!(authEmulatorHost || firestoreEmulatorHost || storageEmulatorHost);
